import React from 'react';
import PropTypes from 'prop-types';

// =============================================================================
// Spinner — v2 loading primitive. Pure-CSS partial-ring rotation, zero AntD
// dependency. Replaces AntD `<Spin>` across every v2 surface.
//
// Modes:
//   · Bare spinner (no children)         — `<Spinner/>` renders a small
//     circular indicator, optionally with a `tip` label below.
//   · Overlay on children (`spinning`)   — wraps children and, while
//     `spinning` is true, overlays a semi-transparent backdrop + centered
//     indicator on top. Children stay in the DOM so layout doesn't shift
//     when the spinner flips off.
//
// Visual: partial ring (arc on top + muted ring behind), Stripe / Linear
// aesthetic. Accent color inherits from `var(--sys)` — adapts to org theme.
// Pure CSS animation via `@keyframes spinner-rotate` — GPU-accelerated, no
// JS animation loop.
//
// Accessibility: wrapper has `role="status"` + `aria-label={tip || 'Loading'}`.
// The spinning glyph itself is `aria-hidden` since its motion alone doesn't
// convey state.
//
// See SPECIFICATION §6 vocabulary + Rule 10 ("never use AntD Spin in v2
// scope"). When AntD is decommissioned long-term, this primitive doesn't
// budge — its API is self-contained.
// =============================================================================

const Spinner = ({
  size = 'default',
  spinning = true,
  tip,
  children,
  fullHeight = false,
  className,
}) => {
  const extraClass = className ? ` ${className}` : '';

  // No children → bare spinner, optionally inside a full-height wrapper.
  if (children === undefined) {
    if (!spinning) return null;
    return (
      <div
        className={`spinner-wrap${fullHeight ? ' spinner-wrap--full' : ''}${extraClass}`}
        role="status"
        aria-label={tip || 'Loading'}
      >
        <span className={`spinner spinner--${size}`} aria-hidden="true" />
        {tip && <div className="spinner-tip">{tip}</div>}
      </div>
    );
  }

  // Children provided → overlay mode. Children always in the DOM; backdrop
  // + indicator layer on top while `spinning`. Parent needs `position:
  // relative` context — the container provides it.
  return (
    <div className={`spinner-container${extraClass}`}>
      {children}
      {spinning && (
        <div
          className="spinner-overlay"
          role="status"
          aria-label={tip || 'Loading'}
        >
          <span className={`spinner spinner--${size}`} aria-hidden="true" />
          {tip && <div className="spinner-tip">{tip}</div>}
        </div>
      )}
    </div>
  );
};

Spinner.propTypes = {
  /** Size of the spinning glyph. Governed 3-step ladder. */
  size: PropTypes.oneOf(['sm', 'default', 'lg']),
  /** Whether the spinner is active. In overlay mode, flipping false removes the overlay; children remain mounted. */
  spinning: PropTypes.bool,
  /** Optional "Loading…" style label rendered below the indicator. Also used as aria-label. */
  tip: PropTypes.node,
  /** When provided, wraps children and renders an overlay backdrop + indicator on top while `spinning`. */
  children: PropTypes.node,
  /** In bare-spinner mode, stretch the wrapper to fill its parent (min-height 200 px, flex-centered). */
  fullHeight: PropTypes.bool,
  /** Extra class passthrough. */
  className: PropTypes.string,
};

// =============================================================================
// OverlaySpinner — full-viewport overlay. For the rare case where a route
// is still mounting and there's nothing useful to show underneath yet.
// Mirrors Tasks' `AppOverlaySpinner`.
// =============================================================================

export const OverlaySpinner = ({ tip = 'Loading…' }) => (
  <div className="spinner-fullpage" role="status" aria-label={tip}>
    <Spinner size="lg" tip={tip} />
  </div>
);

OverlaySpinner.propTypes = {
  tip: PropTypes.node,
};

export default Spinner;
